import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ShoppingBag, Heart, User, Menu, X, Instagram, Facebook, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import api, { currency } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "sonner";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const loc = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [newsletterEmail, setNewsletterEmail] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setSearchOpen(false); }, [loc.pathname]);

  useEffect(() => {
    if (!query.trim()) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search/suggest?q=${encodeURIComponent(query)}`);
        setSuggestions(data);
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    nav(`/shop?q=${encodeURIComponent(query)}`);
    setSearchOpen(false);
  };

  const subscribeNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    try {
      await api.post("/newsletter", { email: newsletterEmail });
      toast.success("Thank you — you're on the list.");
      setNewsletterEmail("");
    } catch {
      toast.error("Please try again");
    }
  };

  const navLinks = [
    { to: "/shop", label: "Shop" },
    { to: "/category/resin-art", label: "Resin Art" },
    { to: "/category/handmade-bags", label: "Bags" },
    { to: "/category/pearl-jewellery", label: "Pearl" },
    { to: "/gallery", label: "Gallery" },
    { to: "/custom-order", label: "Commission" },
    { to: "/about", label: "Journal" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-cream text-charcoal">
      {/* Top ticker */}
      <div className="bg-charcoal text-ivory overflow-hidden">
        <div className="marquee-mask overflow-hidden">
          <div className="flex whitespace-nowrap animate-marquee">
            {Array.from({ length: 2 }).map((_, k) => (
              <div key={k} className="flex items-center gap-16 px-8 py-2.5 text-[11px] tracking-[0.25em] uppercase">
                <span>Complimentary shipping over ₹2,000</span>
                <span>·</span>
                <span>Signed, numbered & delivered in linen</span>
                <span>·</span>
                <span>Handmade in India</span>
                <span>·</span>
                <span>WELCOME10 · First order</span>
                <span>·</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Header */}
      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 transition-[background-color,border-color] duration-300 ${
          scrolled ? "bg-cream/85 backdrop-blur-md border-b divider-gold" : "bg-cream border-b border-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
          <Sheet>
            <SheetTrigger asChild>
              <button data-testid="mobile-nav-open" className="lg:hidden p-2 -ml-2" aria-label="Open menu">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-cream w-[85vw]">
              <div className="pt-8 space-y-6">
                <Link to="/" className="block font-serif text-2xl">Crystal Clear Craft</Link>
                <nav className="flex flex-col gap-4 text-sm">
                  {navLinks.map((l) => (
                    <NavLink key={l.to} to={l.to} className="py-1">{l.label}</NavLink>
                  ))}
                </nav>
              </div>
            </SheetContent>
          </Sheet>

          <Link to="/" data-testid="brand-logo" className="flex flex-col items-center leading-none group">
            <span className="font-serif text-2xl lg:text-[26px] tracking-tight">Crystal Clear Craft</span>
            <span className="overline text-[9px] text-charcoal/60 mt-1 group-hover:text-gold transition-colors">Est · Handmade in India</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8 absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) => `nav-link text-[13px] tracking-wide ${isActive ? "text-emerald" : "text-charcoal/80 hover:text-charcoal"}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button data-testid="open-search" onClick={() => setSearchOpen((v) => !v)} className="p-2 hover:text-emerald transition-colors" aria-label="Search">
              <Search className="w-[18px] h-[18px]" />
            </button>
            <Link to={user ? "/account?tab=wishlist" : "/auth"} data-testid="nav-wishlist" className="p-2 hover:text-emerald transition-colors" aria-label="Wishlist">
              <Heart className="w-[18px] h-[18px]" />
            </Link>
            <Link to={user ? (user.role === "admin" ? "/admin" : "/account") : "/auth"} data-testid="nav-account" className="p-2 hover:text-emerald transition-colors" aria-label="Account">
              <User className="w-[18px] h-[18px]" />
            </Link>
            <Link to="/cart" data-testid="nav-cart" className="p-2 hover:text-emerald transition-colors relative" aria-label="Cart">
              <ShoppingBag className="w-[18px] h-[18px]" />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-emerald text-ivory text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{count}</span>
              )}
            </Link>
            {user && (
              <button data-testid="nav-logout" onClick={() => { logout(); toast.success("Signed out"); nav("/"); }} className="hidden md:block ml-2 text-[11px] tracking-[0.2em] uppercase text-charcoal/60 hover:text-emerald">Logout</button>
            )}
          </div>
        </div>

        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="border-t divider-gold bg-cream overflow-hidden"
            >
              <div className="max-w-[900px] mx-auto px-6 py-6">
                <form onSubmit={submitSearch}>
                  <Input
                    data-testid="search-input"
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search resin, bags, pearls, or by material…"
                    className="border-0 border-b divider-gold rounded-none bg-transparent text-lg py-6 focus-visible:ring-0 font-serif"
                  />
                </form>
                {suggestions.length > 0 && (
                  <div className="mt-4 grid gap-2">
                    {suggestions.map((s) => (
                      <Link
                        key={s.id}
                        to={`/product/${s.slug}`}
                        data-testid={`suggest-${s.slug}`}
                        className="flex items-center gap-4 py-2 hover:bg-ivory/60 px-2"
                      >
                        <img src={s.images?.[0]} alt="" className="w-12 h-12 object-cover" />
                        <div className="flex-1">
                          <div className="text-sm">{s.name}</div>
                          <div className="text-xs text-charcoal/60">{s.category}</div>
                        </div>
                        <div className="text-sm font-serif">{currency(s.price)}</div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-charcoal text-ivory pt-24 pb-10 mt-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16">
            <div className="md:col-span-5">
              <div className="font-serif text-3xl">Crystal Clear Craft</div>
              <p className="mt-6 text-ivory/70 max-w-md leading-relaxed text-[15px]">
                A house of handmade objects — resin art, leather bags, and pearl jewellery — made slowly and shipped
                thoughtfully from our studio in India.
              </p>
              <form onSubmit={subscribeNewsletter} className="mt-10 flex items-center gap-0 max-w-md border-b border-ivory/30 pb-2">
                <input
                  data-testid="newsletter-email"
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="Your email"
                  className="bg-transparent flex-1 py-3 outline-none text-sm placeholder:text-ivory/40"
                  required
                />
                <button data-testid="newsletter-submit" type="submit" className="overline text-gold hover:text-ivory transition-colors">Subscribe</button>
              </form>
            </div>

            <div className="md:col-span-2">
              <div className="overline text-gold mb-5">Shop</div>
              <ul className="space-y-3 text-sm text-ivory/70">
                <li><Link to="/category/resin-art" className="hover:text-ivory">Resin Art</Link></li>
                <li><Link to="/category/handmade-bags" className="hover:text-ivory">Handmade Bags</Link></li>
                <li><Link to="/category/pearl-jewellery" className="hover:text-ivory">Pearl Jewellery</Link></li>
                <li><Link to="/shop?tag=new-arrival" className="hover:text-ivory">New arrivals</Link></li>
                <li><Link to="/shop?tag=best-seller" className="hover:text-ivory">Best sellers</Link></li>
                <li><Link to="/custom-order" className="hover:text-ivory">Custom orders</Link></li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="overline text-gold mb-5">House</div>
              <ul className="space-y-3 text-sm text-ivory/70">
                <li><Link to="/about" className="hover:text-ivory">The Journal</Link></li>
                <li><Link to="/gallery" className="hover:text-ivory">Gallery</Link></li>
                <li><Link to="/contact" className="hover:text-ivory">Contact</Link></li>
                <li><Link to="/auth" className="hover:text-ivory">Sign in</Link></li>
              </ul>
            </div>

            <div className="md:col-span-3">
              <div className="overline text-gold mb-5">Studio</div>
              <p className="text-sm text-ivory/70 leading-relaxed">
                Warispura, kamptee, Nagpur<br />
                Tue – Sat · 10am – 6pm
              </p>
              <div className="mt-6 flex items-center gap-4 text-ivory/70">
                <a href="#" className="hover:text-gold" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
                <a href="#" className="hover:text-gold" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
                <a href="https://wa.me/919000000000" className="hover:text-gold" aria-label="WhatsApp"><MessageCircle className="w-4 h-4" /></a>
              </div>
            </div>
          </div>

          <div className="border-t border-ivory/10 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-ivory/50">
            <div>© {new Date().getFullYear()} Crystal Clear Craft. All rights reserved.</div>
            <div className="flex gap-6"><span>Privacy</span><span>Terms</span><span>Shipping & Returns</span></div>
          </div>
        </div>
      </footer>
    </div>
  );
}
